import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation } from 'react-router-dom';

import { useResetPasswordMutation } from '../store/api/authApi';

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { verifiedToken?: string } | null;
  const verifiedToken = state?.verifiedToken;
  
  const [resetPassword, { isLoading, isSuccess, error }] = useResetPasswordMutation();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false,
  });

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return t('validation.password.required');
    }
    if (password.length < 8) {
      return t('validation.password.minLength');
    }
    if (password.length > 128) {
      return t('validation.password.maxLength');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.password.confirmMismatch');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verifiedToken) {
      return;
    }
    
    if (!validateForm()) {
      setTouched({ password: true, confirmPassword: true });
      return;
    }

    try {
      await resetPassword({
        token: verifiedToken,
        password: formData.password,
      }).unwrap();
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch {
      // Error handled by RTK Query
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }
    
    if (touched[name as keyof typeof touched]) {
      if (name === 'password') {
        const error = validatePassword(value);
        setErrors((prev) => ({
          ...prev,
          password: error,
          confirmPassword: value !== formData.confirmPassword && touched.confirmPassword
            ? t('validation.password.confirmMismatch')
            : prev.confirmPassword,
        }));
      } else if (name === 'confirmPassword') {
        const error = value !== formData.password
          ? t('validation.password.confirmMismatch')
          : undefined;
        setErrors((prev) => ({
          ...prev,
          confirmPassword: error,
        }));
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    
    if (name === 'password') {
      const error = validatePassword(value);
      setErrors((prev) => ({
        ...prev,
        password: error,
      }));
    } else if (name === 'confirmPassword') {
      const error = value !== formData.password
        ? t('validation.password.confirmMismatch')
        : undefined;
      setErrors((prev) => ({
        ...prev,
        confirmPassword: error,
      }));
    }
  };

  if (!verifiedToken) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative">
        <div className="w-full max-w-md animate-scale-in relative z-10">
          <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-heading font-bold text-brand-accent">
                {t('auth.resetPassword.title')}
              </h1>
              <div className="p-4 bg-danger/20 border border-danger rounded-lg">
                <p className="text-red-300 text-sm font-body">
                  {t('auth.resetPassword.noToken')}
                </p>
              </div>
              <Link
                to="/forgot-password"
                className="block w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] transition-all duration-300 text-center"
              >
                {t('auth.resetPassword.requestNew')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="w-full max-w-md animate-scale-in relative z-10">
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2 animate-slide-down">
            <h1 className="text-4xl font-heading font-bold text-brand-accent">
              {t('auth.resetPassword.title')}
            </h1>
            <p className="text-neutral-medium font-body text-sm">
              {t('auth.resetPassword.subtitle')}
            </p>
          </div>

          {/* Success Message */}
          {isSuccess && (
            <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg animate-slide-down">
              <p className="text-green-300 text-sm font-body">
                {t('auth.resetPassword.success')}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && !isSuccess && (
            <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
              <p className="text-red-300 text-sm font-body">
                {error && 'data' in error
                  ? (error.data as { message?: string })?.message
                  : t('errors.network')}
              </p>
            </div>
          )}

          {/* Form */}
          {!isSuccess && (
            <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-5">
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-body font-medium text-neutral-light"
                >
                  {t('auth.resetPassword.newPassword')}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body ${
                    errors.password && touched.password
                      ? 'border-danger focus:ring-danger focus:border-danger'
                      : 'border-neutral-dark focus:border-brand-accent'
                  }`}
                  placeholder="••••••••"
                />
                {errors.password && touched.password && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {errors.password}
                  </p>
                )}
                <p className="text-neutral-medium text-xs font-body">
                  {t('validation.password.hint')}
                </p>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-body font-medium text-neutral-light"
                >
                  {t('auth.resetPassword.confirmPassword')}
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 transform focus:scale-[1.02] font-body ${
                    errors.confirmPassword && touched.confirmPassword
                      ? 'border-danger focus:ring-danger focus:border-danger'
                      : 'border-neutral-dark focus:border-brand-accent'
                  }`}
                  placeholder="••••••••"
                />
                {errors.confirmPassword && touched.confirmPassword && (
                  <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('common.loading')}
                  </span>
                ) : (
                  t('auth.resetPassword.submit')
                )}
              </button>
            </form>
          )}

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-body"
            >
              {t('auth.resetPassword.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
